/**
 * MyTripLogsPage — Support-Worker-facing Vehicle Log web page.
 *
 * Scope:
 *   • Shows the logged-in worker's OWN trips only (`GET /api/transport/logs`
 *     — backend silently scopes to current_user.id for non-view_all callers).
 *   • Provides a "Log New Trip" button that opens the trip form inside the
 *     Staff Portal (`/staff/transport/new`, BS3-026), passing on ?shift_id=.
 *
 * Strict non-goals (per scope contract):
 *   • Does NOT call any /admin/transport/* endpoint.
 *   • Does NOT expose Approve / Reject / Settings actions to the SW.
 *   • Does NOT call /transport/summary (admin-only via transport.view_all).
 *
 * Permission gates:
 *   • Sidebar entry requires `transport.log` OR `transport.view_own`.
 *   • Backend already enforces `transport.view_own` on every read.
 *   • If master flag `transport_logging_enabled` is OFF, the backend returns
 *     403 FEATURE_DISABLED and we render the disabled card.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Plus, Car, AlertTriangle, Info, Route, CheckCircle2, Clock, XCircle } from 'lucide-react';
import { EmptyState } from '@/components/common/EmptyState';
import { TableSkeleton } from '@/components/common/TableSkeleton';
import { toast } from 'sonner';

const STATUS_COLORS = {
  DRAFT: 'bg-gray-100 text-gray-700',
  SUBMITTED: 'bg-blue-100 text-blue-700',
  AUTO_APPROVED: 'bg-emerald-100 text-emerald-700',
  APPROVED: 'bg-green-100 text-green-700',
  REJECTED: 'bg-red-100 text-red-700',
};

function StatusBadge({ status }) {
  return (
    <Badge
      className={`${STATUS_COLORS[status] || 'bg-gray-100 text-gray-700'} text-xs`}
      data-testid={`my-trip-status-${status}`}
    >
      {status}
    </Badge>
  );
}

const TABS = ['ALL', 'SUBMITTED', 'APPROVED', 'AUTO_APPROVED', 'REJECTED'];

export default function MyTripLogsPage() {
  const navigate = useNavigate();
  // BS3-026: the staff-portal trip form; a shift opened from Shift Detail (?shift_id=) is passed on so the trip stays NDIS-billable
  const [search] = useSearchParams();
  const newTripUrl = search.get('shift_id') ? `/staff/transport/new?shift_id=${encodeURIComponent(search.get('shift_id'))}` : '/staff/transport/new';
  const [tab, setTab] = useState('ALL');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [featureDisabled, setFeatureDisabled] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFeatureDisabled(false);
    try {
      const params = tab === 'ALL' ? {} : { status: tab };
      // No staff_id passed — backend scopes to caller for non-view_all users.
      const r = await api.get('/transport/logs', { params });
      setLogs(r.data?.data || []);
    } catch (e) {
      const code = e?.response?.data?.error?.code;
      if (code === 'FEATURE_DISABLED' || e?.response?.status === 403) {
        if (code === 'FEATURE_DISABLED') {
          setFeatureDisabled(true);
        } else {
          toast.error('You do not have permission to view trip logs');
        }
      } else {
        toast.error('Failed to load your trips');
      }
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  if (featureDisabled) {
    return (
      <div className="container mx-auto p-6 max-w-5xl" data-testid="my-trip-logs-feature-disabled">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Vehicle Log Disabled
            </CardTitle>
            <CardDescription>
              The Vehicle Log module is currently turned off for your organisation.
              Please ask your administrator to enable it.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-5xl" data-testid="my-trip-logs-page">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Car className="h-6 w-6 text-primary" /> My Trip Logs
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Your personal vehicle log history. Submit new trips during or after your shifts.
          </p>
        </div>
        <Button
          onClick={() => navigate(newTripUrl)}
          className="gap-2"
          data-testid="my-trip-logs-new-button"
        >
          <Plus className="h-4 w-4" /> Log New Trip
        </Button>
      </div>

      <Alert className="mb-4 bg-blue-50/40 border-blue-200">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-xs text-blue-900">
          You can only see your own trips here. Approval is handled by your administrator.
        </AlertDescription>
      </Alert>

      {/* KPI band — polish pass (additive testids only). */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-testid="my-trip-logs-summary">
        <div className="rounded-lg border bg-white p-3" data-testid="my-trip-logs-summary-total">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Total trips</span>
            <Route className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          </div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-foreground">{logs.length}</div>
        </div>
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3" data-testid="my-trip-logs-summary-approved">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Approved</span>
            <CheckCircle2 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          </div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-emerald-700">{logs.filter(l => l.status === 'APPROVED' || l.status === 'AUTO_APPROVED').length}</div>
        </div>
        <div className="rounded-lg border border-sky-200 bg-sky-50/40 p-3" data-testid="my-trip-logs-summary-pending">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Pending</span>
            <Clock className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          </div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-sky-700">{logs.filter(l => l.status === 'SUBMITTED').length}</div>
        </div>
        <div className={`rounded-lg border p-3 ${logs.filter(l => l.status === 'REJECTED').length > 0 ? 'border-red-200 bg-red-50/40' : 'border-border bg-white'}`} data-testid="my-trip-logs-summary-rejected">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Rejected</span>
            <XCircle className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          </div>
          <div className={`mt-1 text-xl font-semibold tabular-nums ${logs.filter(l => l.status === 'REJECTED').length > 0 ? 'text-red-700' : 'text-foreground'}`}>{logs.filter(l => l.status === 'REJECTED').length}</div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList data-testid="my-trip-logs-tabs">
          {TABS.map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`tab-${t.toLowerCase()}`}>
              {t === 'ALL' ? 'All' : t.replace('_', ' ').toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase())}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <TableSkeleton rows={4} columns={5} testid="my-trip-logs-loading" />
          ) : logs.length === 0 ? (
            <EmptyState
              icon={Car}
              title="No trips to show"
              description={tab === 'ALL' ? 'Log a trip after a shift to track your kilometres.' : `No trips with status ${tab.replace('_', ' ').toLowerCase()}.`}
              action={tab === 'ALL' ? <Button size="sm" onClick={() => navigate(newTripUrl)}><Plus className="h-4 w-4 mr-1" />Log new trip</Button> : null}
              testid="my-trip-logs-empty"
            />
          ) : (
            <Table data-testid="my-trip-logs-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Purpose</TableHead>
                  <TableHead className="text-right">Distance</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((t) => (
                  <TableRow key={t.id || t._id} data-testid={`my-trip-row-${t.id || t._id}`}>
                    <TableCell className="text-sm">{t.date || '-'}</TableCell>
                    <TableCell className="text-sm">{t.purpose || '-'}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      {typeof t.distance_km === 'number' ? `${t.distance_km.toFixed(1)} km` : '-'}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {t.vehicle_kind || '-'}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={t.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
